import { useContext, useState } from 'react';
import './SelectedItems.css';
import { resolveImageSrc } from '../../api/client';
import { formatDollars } from '../../shared/functions/formatter';
import { Button, Card, CardTitle, Dialog, EmptyState, IconButton, Meta, Stack } from '../../ui';
import { CashierContext } from '../Cashier';
import ComputeChange from '../modals/ComputeChange';
import ConfirmRecord from '../modals/ConfirmRecord';
import { ChangeGlyph, RecordGlyph, ResetGlyph } from './tillIcons';

const DeleteIcon = () => (
  <div className="iconContainer">
    <svg
      clipRule="evenodd"
      fillRule="evenodd"
      strokeLinejoin="round"
      strokeMiterlimit="2"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="m12.002 2.005c5.518 0 9.998 4.48 9.998 9.997 0 5.518-4.48 9.998-9.998 9.998-5.517 0-9.997-4.48-9.997-9.998 0-5.517 4.48-9.997 9.997-9.997zm0 8.933-2.721-2.722c-.146-.146-.339-.219-.531-.219-.404 0-.75.324-.75.749 0 .193.073.384.219.531l2.722 2.722-2.728 2.728c-.147.147-.22.34-.22.531 0 .427.35.75.751.75.192 0 .384-.073.53-.219l2.728-2.728 2.729 2.728c.146.146.338.219.53.219.401 0 .75-.323.75-.75 0-.191-.073-.384-.22-.531l-2.727-2.728 2.717-2.717c.146-.147.219-.338.219-.531 0-.425-.346-.75-.75-.75-.192 0-.385.073-.531.22z"
        fillRule="nonzero"
      />
    </svg>
  </div>
);

const ResetIcon = () => (
  <div className="iconContainer">
    <ResetGlyph />
    &nbsp;&nbsp;RESET
  </div>
);

const WriteIcon = () => (
  <div className="iconContainer">
    <RecordGlyph />
    &nbsp;&nbsp;RECORD
  </div>
);

const CalculateIcon = () => (
  <div className="iconContainer">
    <ChangeGlyph />
    &nbsp;&nbsp;CHANGE
  </div>
);

const SelectedItems = () => {
  const {
    state: { items, bundles, total, savings },
    dispatch,
    completeOrder,
    canUndo,
    applyUndo,
  } = useContext(CashierContext);
  const [showDialog, setShowDialog] = useState(false);
  const [showComputeChange, setShowComputeChange] = useState(false);
  const appliedLabel = bundles.map((bundle) => (bundle.quantity > 1 ? bundle.quantity + '× ' + bundle.title : bundle.title)).join(', ');
  return (
    <Card className="selectedItemsContainer">
      <CardTitle>Current order</CardTitle>
      {items.length > 0 && <Meta className="cartCount">{items.length === 1 ? '1 item' : items.length + ' items'}</Meta>}
      <div className="tableContainer">
        <table className="cashierStateTable">
          <thead>
            <tr>
              <th>Icon</th>
              <th align="left">
                <span className="cartNameFull">Name</span>
                <span className="cartNameShort">Item</span>
              </th>
              <th>Price</th>
              <th>Qty</th>
              <th>Less</th>
              <th>Sub</th>
            </tr>
          </thead>
          <tbody>
            {items.map(({ id, img, price, title, quantity, subtotal }) => (
              <tr key={id}>
                <td>
                  <img className="cashierStateItemImg" src={resolveImageSrc(img)} alt={title} width={'30px'} />
                </td>
                <td align="left">{title}</td>
                <td className="price money">{formatDollars(price)}</td>
                <td>{quantity > 1 ? <div className="qty">{quantity}</div> : quantity}</td>
                <td>
                  <IconButton
                    label={'Remove ' + title}
                    className="cartRemove"
                    onClick={() =>
                      dispatch({
                        type: 'REMOVE',
                        payload: { id, price } as Item,
                      })
                    }
                  >
                    <DeleteIcon />
                  </IconButton>
                </td>
                <td align="right" className="subtotal money">
                  <div>{formatDollars(subtotal)}</div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={6}>
                  <EmptyState title="no items in cart" />
                </td>
              </tr>
            )}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              {/* Savings first, then the total, so the last figure read is what
                  the customer actually pays. Both rows use the same shape — a
                  label across the leading columns, the amount in the final one —
                  which is what keeps the two amounts on the same right edge as
                  the item subtotals instead of floating in a merged cell. */}
              {savings > 0 && (
                <tr className="bundleSavingsRow">
                  <td />
                  {/* The label names what was discounted; the amount keeps the
                      same two-column span as the total below it so the two line
                      up. */}
                  <td colSpan={3} align="right" title={appliedLabel}>
                    {appliedLabel} savings
                  </td>
                  <td colSpan={2} align="right" className="money bundleSavings">
                    <div>{formatDollars(savings)}</div>
                  </td>
                </tr>
              )}
              <tr className="cartTotalRow">
                <td />
                <td colSpan={3} align="right">
                  Total:
                </td>
                {/* Two columns, not one: the 2em total is wider than the Sub
                    column alone and would otherwise overflow to the right of
                    the table instead of lining up under the subtotals. */}
                <td colSpan={2} align="right" className="money">
                  <div>{formatDollars(total)}</div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <Stack direction="row" align="end" className="resultContainer">
        <Button variant="ghost" className="tillAction" onClick={() => dispatch({ type: 'RESET' })}>
          <ResetIcon />
        </Button>
        <Button
          variant="ghost"
          className="tillAction"
          onClick={() => setShowDialog(true)}
          // Nothing to record with an empty cart. The server still refuses an
          // empty sale; this just stops the dialog opening on a dead end.
          disabled={items.length === 0}
          title={items.length === 0 ? 'Add an item first.' : undefined}
        >
          <WriteIcon />
        </Button>
        <Button variant="ghost" className="tillAction" onClick={() => setShowComputeChange(true)}>
          <CalculateIcon />
        </Button>
      </Stack>
      {canUndo && (
        <div className="cartUndo" role="status">
          <span>Order cleared.</span>
          <Button variant="secondary" size="sm" onClick={applyUndo}>
            Undo
          </Button>
        </div>
      )}
      <Dialog open={showDialog} onOpenChange={setShowDialog} title="Confirm Record" size="lg">
        {/* An explicit way out: Escape and a backdrop click already worked, but
            nothing on screen said so and a touch till has no visible Escape. */}
        <div className="ui-dialog-dismiss">
          <Button variant="secondary" size="sm" onClick={() => setShowDialog(false)}>
            Close
          </Button>
        </div>
        {/* Recording or queuing a sale finishes the order, so clear the cart —
            otherwise the persisted cart would offer to sell it a second time. */}
        <ConfirmRecord
          onRecorded={() => {
            // Tell the display the order finished before the cart clears, so it
            // can thank the customer instead of showing an empty basket. The
            // clear is RECORDED, not RESET, so a paid order never offers undo.
            completeOrder();
            dispatch({ type: 'RECORDED' });
          }}
        />
      </Dialog>
      <Dialog open={showComputeChange} onOpenChange={setShowComputeChange} title="Change" size="lg">
        <div className="ui-dialog-dismiss">
          <Button variant="secondary" size="sm" onClick={() => setShowComputeChange(false)}>
            Close
          </Button>
        </div>
        <ComputeChange />
      </Dialog>
    </Card>
  );
};

export default SelectedItems;
